/**
 * Plant Loop IDF Field Mappings
 *
 * Maps plant component fields to their IDF object field positions.
 */

import type { PlantComponentIDFMapping } from './types';

/**
 * IDF field mappings for all plant components
 */
export const PLANT_FIELD_MAPPINGS: PlantComponentIDFMapping[] = [
  // ============================================
  // CHILLERS
  // ============================================
  {
    componentType: 'chiller-electric-eir',
    idfClass: 'Chiller:Electric:EIR',
    nameFieldIndex: 0,
    totalFields: 40,
    fieldMappings: {
      'Reference Capacity': { fieldIndex: 1, defaultValue: 'autosize' },
      'Reference COP': { fieldIndex: 2, defaultValue: '5.5' },
      'Reference Leaving Chilled Water Temperature': { fieldIndex: 3, defaultValue: '6.67' },
      'Reference Entering Condenser Fluid Temperature': { fieldIndex: 4, defaultValue: '29.4' },
      'Reference Chilled Water Flow Rate': { fieldIndex: 5, defaultValue: 'autosize' },
      'Reference Condenser Fluid Flow Rate': { fieldIndex: 6, defaultValue: 'autosize' },
      'Condenser Type': { fieldIndex: 23, defaultValue: 'WaterCooled' },
    },
    portMappings: [
      { portId: 'chw-inlet', fieldIndex: 14, fieldName: 'Chilled Water Inlet Node Name' },
      { portId: 'chw-outlet', fieldIndex: 15, fieldName: 'Chilled Water Outlet Node Name' },
      { portId: 'cw-inlet', fieldIndex: 16, fieldName: 'Condenser Inlet Node Name' },
      { portId: 'cw-outlet', fieldIndex: 17, fieldName: 'Condenser Outlet Node Name' },
    ],
  },
  {
    componentType: 'chiller-electric-reformulated-eir',
    idfClass: 'Chiller:Electric:ReformulatedEIR',
    nameFieldIndex: 0,
    totalFields: 36,
    fieldMappings: {
      'Reference Capacity': { fieldIndex: 1, defaultValue: 'autosize' },
      'Reference COP': { fieldIndex: 2, defaultValue: '5.5' },
      'Reference Leaving Chilled Water Temperature': { fieldIndex: 3, defaultValue: '6.67' },
      'Reference Leaving Condenser Water Temperature': { fieldIndex: 4, defaultValue: '35.0' },
      'Reference Chilled Water Flow Rate': { fieldIndex: 5, defaultValue: 'autosize' },
      'Reference Condenser Water Flow Rate': { fieldIndex: 6, defaultValue: 'autosize' },
    },
    portMappings: [
      { portId: 'chw-inlet', fieldIndex: 15, fieldName: 'Chilled Water Inlet Node Name' },
      { portId: 'chw-outlet', fieldIndex: 16, fieldName: 'Chilled Water Outlet Node Name' },
      { portId: 'cw-inlet', fieldIndex: 17, fieldName: 'Condenser Inlet Node Name' },
      { portId: 'cw-outlet', fieldIndex: 18, fieldName: 'Condenser Outlet Node Name' },
    ],
  },
  {
    componentType: 'chiller-absorption',
    idfClass: 'Chiller:Absorption',
    nameFieldIndex: 0,
    totalFields: 27,
    fieldMappings: {
      'Nominal Capacity': { fieldIndex: 1, defaultValue: 'autosize' },
      'Nominal Pumping Power': { fieldIndex: 2, defaultValue: 'autosize' },
      'Design Chilled Water Flow Rate': { fieldIndex: 8, defaultValue: 'autosize' },
      'Design Condenser Water Flow Rate': { fieldIndex: 9, defaultValue: 'autosize' },
      'Coefficient of Performance': { fieldIndex: 7, defaultValue: '0.7' },
    },
    portMappings: [
      { portId: 'chw-inlet', fieldIndex: 3, fieldName: 'Chilled Water Inlet Node Name' },
      { portId: 'chw-outlet', fieldIndex: 4, fieldName: 'Chilled Water Outlet Node Name' },
      { portId: 'cw-inlet', fieldIndex: 5, fieldName: 'Condenser Inlet Node Name' },
      { portId: 'cw-outlet', fieldIndex: 6, fieldName: 'Condenser Outlet Node Name' },
    ],
  },

  // ============================================
  // BOILERS
  // ============================================
  {
    componentType: 'boiler-hot-water',
    idfClass: 'Boiler:HotWater',
    nameFieldIndex: 0,
    totalFields: 18,
    fieldMappings: {
      'Fuel Type': { fieldIndex: 1, defaultValue: 'NaturalGas' },
      'Nominal Capacity': { fieldIndex: 2, defaultValue: 'autosize' },
      'Nominal Thermal Efficiency': { fieldIndex: 3, defaultValue: '0.80' },
      'Efficiency Curve Temperature Evaluation Variable': { fieldIndex: 4, defaultValue: 'LeavingBoiler' },
      'Design Water Flow Rate': { fieldIndex: 9, defaultValue: 'autosize' },
      'Minimum Part Load Ratio': { fieldIndex: 12, defaultValue: '0.0' },
      'Maximum Part Load Ratio': { fieldIndex: 13, defaultValue: '1.1' },
      'Boiler Flow Mode': { fieldIndex: 16, defaultValue: 'NotModulated' },
    },
    portMappings: [
      { portId: 'water-inlet', fieldIndex: 10, fieldName: 'Boiler Water Inlet Node Name' },
      { portId: 'water-outlet', fieldIndex: 11, fieldName: 'Boiler Water Outlet Node Name' },
    ],
  },

  // ============================================
  // COOLING TOWERS
  // ============================================
  {
    componentType: 'cooling-tower-single-speed',
    idfClass: 'CoolingTower:SingleSpeed',
    nameFieldIndex: 0,
    totalFields: 38,
    fieldMappings: {
      'Design Water Flow Rate': { fieldIndex: 3, defaultValue: 'autosize' },
      'Design Air Flow Rate': { fieldIndex: 4, defaultValue: 'autosize' },
      'Design Fan Power': { fieldIndex: 5, defaultValue: 'autosize' },
      'Design U-Factor Times Area Value': { fieldIndex: 6, defaultValue: 'autosize' },
      'Performance Input Method': { fieldIndex: 16, defaultValue: 'UFactorTimesAreaAndDesignWaterFlowRate' },
    },
    portMappings: [
      { portId: 'water-inlet', fieldIndex: 1, fieldName: 'Water Inlet Node Name' },
      { portId: 'water-outlet', fieldIndex: 2, fieldName: 'Water Outlet Node Name' },
    ],
  },
  {
    componentType: 'cooling-tower-two-speed',
    idfClass: 'CoolingTower:TwoSpeed',
    nameFieldIndex: 0,
    totalFields: 45,
    fieldMappings: {
      'Design Water Flow Rate': { fieldIndex: 3, defaultValue: 'autosize' },
      'High Fan Speed Air Flow Rate': { fieldIndex: 4, defaultValue: 'autosize' },
      'High Fan Speed Fan Power': { fieldIndex: 5, defaultValue: 'autosize' },
      'Low Fan Speed Air Flow Rate': { fieldIndex: 10, defaultValue: 'autosize' },
      'Low Fan Speed Fan Power': { fieldIndex: 11, defaultValue: 'autosize' },
      'Performance Input Method': { fieldIndex: 20, defaultValue: 'UFactorTimesAreaAndDesignWaterFlowRate' },
    },
    portMappings: [
      { portId: 'water-inlet', fieldIndex: 1, fieldName: 'Water Inlet Node Name' },
      { portId: 'water-outlet', fieldIndex: 2, fieldName: 'Water Outlet Node Name' },
    ],
  },
  {
    componentType: 'cooling-tower-variable-speed',
    idfClass: 'CoolingTower:VariableSpeed',
    nameFieldIndex: 0,
    totalFields: 31,
    fieldMappings: {
      'Design Water Flow Rate': { fieldIndex: 5, defaultValue: 'autosize' },
      'Design Air Flow Rate': { fieldIndex: 6, defaultValue: 'autosize' },
      'Design Fan Power': { fieldIndex: 7, defaultValue: 'autosize' },
      'Model Type': { fieldIndex: 3, defaultValue: 'YorkCalc' },
    },
    portMappings: [
      { portId: 'water-inlet', fieldIndex: 1, fieldName: 'Water Inlet Node Name' },
      { portId: 'water-outlet', fieldIndex: 2, fieldName: 'Water Outlet Node Name' },
    ],
  },
  {
    componentType: 'fluid-cooler-single-speed',
    idfClass: 'FluidCooler:SingleSpeed',
    nameFieldIndex: 0,
    totalFields: 13,
    fieldMappings: {
      'Design Water Flow Rate': { fieldIndex: 3, defaultValue: 'autosize' },
      'Design Air Flow Rate': { fieldIndex: 5, defaultValue: 'autosize' },
      'Design Air Flow Rate Fan Power': { fieldIndex: 6, defaultValue: 'autosize' },
      'Performance Input Method': { fieldIndex: 4, defaultValue: 'UFactorTimesAreaAndDesignWaterFlowRate' },
    },
    portMappings: [
      { portId: 'water-inlet', fieldIndex: 1, fieldName: 'Water Inlet Node Name' },
      { portId: 'water-outlet', fieldIndex: 2, fieldName: 'Water Outlet Node Name' },
    ],
  },

  // ============================================
  // HEAT EXCHANGERS
  // ============================================
  {
    componentType: 'heat-exchanger-fluid-to-fluid',
    idfClass: 'HeatExchanger:FluidToFluid',
    nameFieldIndex: 0,
    totalFields: 20,
    fieldMappings: {
      'Heat Exchange Model Type': { fieldIndex: 9, defaultValue: 'Ideal' },
      'Loop Demand Side Design Flow Rate': { fieldIndex: 7, defaultValue: 'autosize' },
      'Loop Supply Side Design Flow Rate': { fieldIndex: 8, defaultValue: 'autosize' },
      'Control Type': { fieldIndex: 13, defaultValue: 'UncontrolledOn' },
    },
    portMappings: [
      { portId: 'loop-supply-inlet', fieldIndex: 5, fieldName: 'Loop Supply Side Inlet Node Name' },
      { portId: 'loop-supply-outlet', fieldIndex: 6, fieldName: 'Loop Supply Side Outlet Node Name' },
      { portId: 'loop-demand-inlet', fieldIndex: 2, fieldName: 'Loop Demand Side Inlet Node Name' },
      { portId: 'loop-demand-outlet', fieldIndex: 3, fieldName: 'Loop Demand Side Outlet Node Name' },
    ],
  },
  {
    componentType: 'ground-heat-exchanger-system',
    idfClass: 'GroundHeatExchanger:System',
    nameFieldIndex: 0,
    totalFields: 113,
    fieldMappings: {
      'Design Flow Rate': { fieldIndex: 3, defaultValue: 'autosize' },
      'Number of Bore Holes': { fieldIndex: 4, defaultValue: '100' },
      'Bore Hole Length': { fieldIndex: 5, defaultValue: '76.2' },
      'Bore Hole Radius': { fieldIndex: 6, defaultValue: '0.0635' },
      'Ground Thermal Conductivity': { fieldIndex: 7, defaultValue: '2.493' },
      'Ground Thermal Heat Capacity': { fieldIndex: 8, defaultValue: '2.5E+06' },
    },
    portMappings: [
      { portId: 'water-inlet', fieldIndex: 1, fieldName: 'Inlet Node Name' },
      { portId: 'water-outlet', fieldIndex: 2, fieldName: 'Outlet Node Name' },
    ],
  },

  // ============================================
  // PUMPS
  // ============================================
  {
    componentType: 'pump-variable-speed',
    idfClass: 'Pump:VariableSpeed',
    nameFieldIndex: 0,
    totalFields: 30,
    fieldMappings: {
      'Design Maximum Flow Rate': { fieldIndex: 3, defaultValue: 'autosize' },
      'Design Pump Head': { fieldIndex: 4, defaultValue: '179352' },
      'Design Power Consumption': { fieldIndex: 5, defaultValue: 'autosize' },
      'Motor Efficiency': { fieldIndex: 6, defaultValue: '0.9' },
      'Fraction of Motor Inefficiencies to Fluid Stream': { fieldIndex: 7, defaultValue: '0.0' },
      'Pump Control Type': { fieldIndex: 12, defaultValue: 'Intermittent' },
      'VFD Control Type': { fieldIndex: 17, defaultValue: 'PressureSetPointControl' },
    },
    portMappings: [
      { portId: 'water-inlet', fieldIndex: 1, fieldName: 'Inlet Node Name' },
      { portId: 'water-outlet', fieldIndex: 2, fieldName: 'Outlet Node Name' },
    ],
  },
  {
    componentType: 'pump-constant-speed',
    idfClass: 'Pump:ConstantSpeed',
    nameFieldIndex: 0,
    totalFields: 19,
    fieldMappings: {
      'Design Flow Rate': { fieldIndex: 3, defaultValue: 'autosize' },
      'Design Pump Head': { fieldIndex: 4, defaultValue: '179352' },
      'Design Power Consumption': { fieldIndex: 5, defaultValue: 'autosize' },
      'Motor Efficiency': { fieldIndex: 6, defaultValue: '0.9' },
      'Fraction of Motor Inefficiencies to Fluid Stream': { fieldIndex: 7, defaultValue: '0.0' },
      'Pump Control Type': { fieldIndex: 11, defaultValue: 'Intermittent' },
    },
    portMappings: [
      { portId: 'water-inlet', fieldIndex: 1, fieldName: 'Inlet Node Name' },
      { portId: 'water-outlet', fieldIndex: 2, fieldName: 'Outlet Node Name' },
    ],
  },
  {
    componentType: 'headered-pumps-variable-speed',
    idfClass: 'HeaderedPumps:VariableSpeed',
    nameFieldIndex: 0,
    totalFields: 23,
    fieldMappings: {
      'Total Design Flow Rate': { fieldIndex: 3, defaultValue: 'autosize' },
      'Number of Pumps in Bank': { fieldIndex: 4, defaultValue: '2' },
      'Design Pump Head': { fieldIndex: 6, defaultValue: '179352' },
      'Design Power Consumption': { fieldIndex: 7, defaultValue: 'autosize' },
      'Motor Efficiency': { fieldIndex: 8, defaultValue: '0.9' },
      'Flow Sequencing Control Scheme': { fieldIndex: 5, defaultValue: 'Sequential' },
    },
    portMappings: [
      { portId: 'water-inlet', fieldIndex: 1, fieldName: 'Inlet Node Name' },
      { portId: 'water-outlet', fieldIndex: 2, fieldName: 'Outlet Node Name' },
    ],
  },
  {
    componentType: 'headered-pumps-constant-speed',
    idfClass: 'HeaderedPumps:ConstantSpeed',
    nameFieldIndex: 0,
    totalFields: 18,
    fieldMappings: {
      'Total Design Flow Rate': { fieldIndex: 3, defaultValue: 'autosize' },
      'Number of Pumps in Bank': { fieldIndex: 4, defaultValue: '2' },
      'Design Pump Head': { fieldIndex: 6, defaultValue: '179352' },
      'Design Power Consumption': { fieldIndex: 7, defaultValue: 'autosize' },
      'Motor Efficiency': { fieldIndex: 8, defaultValue: '0.9' },
      'Flow Sequencing Control Scheme': { fieldIndex: 5, defaultValue: 'Sequential' },
    },
    portMappings: [
      { portId: 'water-inlet', fieldIndex: 1, fieldName: 'Inlet Node Name' },
      { portId: 'water-outlet', fieldIndex: 2, fieldName: 'Outlet Node Name' },
    ],
  },

  // ============================================
  // DISTRIBUTION
  // ============================================
  {
    componentType: 'pipe-adiabatic',
    idfClass: 'Pipe:Adiabatic',
    nameFieldIndex: 0,
    totalFields: 3,
    fieldMappings: {},
    portMappings: [
      { portId: 'inlet', fieldIndex: 1, fieldName: 'Inlet Node Name' },
      { portId: 'outlet', fieldIndex: 2, fieldName: 'Outlet Node Name' },
    ],
  },

  // ============================================
  // COILS (Demand side)
  // ============================================
  {
    componentType: 'coil-cooling-water-plant',
    idfClass: 'Coil:Cooling:Water',
    nameFieldIndex: 0,
    totalFields: 17,
    fieldMappings: {
      'Design Water Flow Rate': { fieldIndex: 2, defaultValue: 'autosize' },
      'Design Air Flow Rate': { fieldIndex: 3, defaultValue: 'autosize' },
      'Design Inlet Water Temperature': { fieldIndex: 4, defaultValue: '7.22' },
      'Design Inlet Air Temperature': { fieldIndex: 5, defaultValue: '24.0' },
      'Design Outlet Air Temperature': { fieldIndex: 6, defaultValue: '12.8' },
    },
    portMappings: [
      { portId: 'water-inlet', fieldIndex: 9, fieldName: 'Water Inlet Node Name' },
      { portId: 'water-outlet', fieldIndex: 10, fieldName: 'Water Outlet Node Name' },
    ],
  },
  {
    componentType: 'coil-heating-water-plant',
    idfClass: 'Coil:Heating:Water',
    nameFieldIndex: 0,
    totalFields: 16,
    fieldMappings: {
      'U-Factor Times Area Value': { fieldIndex: 2, defaultValue: 'autosize' },
      'Maximum Water Flow Rate': { fieldIndex: 3, defaultValue: 'autosize' },
      'Rated Inlet Water Temperature': { fieldIndex: 8, defaultValue: '82.2' },
      'Rated Inlet Air Temperature': { fieldIndex: 9, defaultValue: '16.6' },
      'Rated Outlet Water Temperature': { fieldIndex: 10, defaultValue: '71.1' },
      'Rated Outlet Air Temperature': { fieldIndex: 11, defaultValue: '32.2' },
    },
    portMappings: [
      { portId: 'water-inlet', fieldIndex: 4, fieldName: 'Water Inlet Node Name' },
      { portId: 'water-outlet', fieldIndex: 5, fieldName: 'Water Outlet Node Name' },
    ],
  },
  {
    componentType: 'water-heater-mixed',
    idfClass: 'WaterHeater:Mixed',
    nameFieldIndex: 0,
    totalFields: 42,
    fieldMappings: {
      'Tank Volume': { fieldIndex: 1, defaultValue: '0.3785' },
      'Deadband Temperature Difference': { fieldIndex: 3, defaultValue: '2.0' },
      'Maximum Temperature Limit': { fieldIndex: 4, defaultValue: '82.2' },
      'Heater Control Type': { fieldIndex: 5, defaultValue: 'Cycle' },
      'Heater Maximum Capacity': { fieldIndex: 6, defaultValue: 'autosize' },
      'Heater Fuel Type': { fieldIndex: 8, defaultValue: 'Electricity' },
      'Heater Thermal Efficiency': { fieldIndex: 9, defaultValue: '1.0' },
    },
    portMappings: [
      { portId: 'use-inlet', fieldIndex: 30, fieldName: 'Use Side Inlet Node Name' },
      { portId: 'use-outlet', fieldIndex: 31, fieldName: 'Use Side Outlet Node Name' },
      { portId: 'source-inlet', fieldIndex: 33, fieldName: 'Source Side Inlet Node Name' },
      { portId: 'source-outlet', fieldIndex: 34, fieldName: 'Source Side Outlet Node Name' },
    ],
  },
];

/**
 * Component type to mapping lookup
 */
export const PLANT_MAPPING_BY_TYPE = new Map<string, PlantComponentIDFMapping>(
  PLANT_FIELD_MAPPINGS.map((m) => [m.componentType, m])
);

/**
 * IDF class to mapping lookup
 */
export const PLANT_MAPPING_BY_CLASS = new Map<string, PlantComponentIDFMapping>(
  PLANT_FIELD_MAPPINGS.map((m) => [m.idfClass, m])
);

/**
 * Get mapping by component type
 */
export function getPlantComponentMapping(componentType: string): PlantComponentIDFMapping | undefined {
  return PLANT_MAPPING_BY_TYPE.get(componentType);
}

/**
 * Get mapping by IDF class
 */
export function getPlantMappingByIDFClass(idfClass: string): PlantComponentIDFMapping | undefined {
  return PLANT_MAPPING_BY_CLASS.get(idfClass);
}

/**
 * Check if an IDF class is a plant component
 */
export function isPlantClass(idfClass: string): boolean {
  return PLANT_MAPPING_BY_CLASS.has(idfClass);
}

/**
 * Get all plant-related IDF classes
 */
export function getPlantIDFClasses(): string[] {
  return PLANT_FIELD_MAPPINGS.map((m) => m.idfClass);
}
